import { useApolloClient } from "@apollo/client"
import { useChannelContext } from "client/components/channel"
import {
  EntryByGiveawayIdDocument,
  type EntryByGiveawayIdQuery,
  type EntryByGiveawayIdQueryVariables,
  type EntryFieldsFragment,
  type GiveawayFieldsFragment,
  Giveaways_EntryEventType,
  Giveaways_GiveawayEventType,
  Giveaways_GiveawayStatus,
  OpenGiveawaysDocument,
  type OpenGiveawaysQuery,
  type OpenGiveawaysQueryVariables,
  useCreateEntryMutation,
  useEntryByGiveawayIdQuery,
  useEntryEventSubscription,
  useGiveawayEventSubscription,
  useOpenGiveawaysQuery,
} from "client/graphql/giveaways"
import { useChatContext } from "client/packages/chat/components/ChatContext"
import { createContext, type FC, type ReactNode, useContext } from "react"

type Props = {
  children: ReactNode
}

type GiveawaysState = {
  giveaway: GiveawayFieldsFragment | null
  entry: EntryFieldsFragment | null
  mutations: {
    enterGiveaway: ReturnType<typeof useCreateEntryMutation>
  }
}

/**
 * Context for managing giveaway state and operations.
 */
const GiveawaysContext = createContext<GiveawaysState | undefined>(
  undefined,
)

/**
 * Provider component that manages giveaway state, subscriptions, and mutations.
 * Handles real-time updates for giveaway events and entries.
 */
const GiveawaysProvider: FC<Props> = ({ children }) => {
  /**
   * Dependencies
   */
  const apolloClient = useApolloClient()
  const { addLocalNotification } = useChatContext()
  const channel = useChannelContext()

  /**
   * Queries
   */
  const { data: openGiveawaysQueryData } = useOpenGiveawaysQuery({
    skip: !channel.broadcastId,
    variables: {
      broadcastId: channel.broadcastId ?? "",
    },
  })

  // Although the API supports multiple open giveaways, this client displays
  // only the most recent one to simplify the UX.
  const giveaway =
    openGiveawaysQueryData?.giveaways_giveaways?.records?.[0] ?? null

  const { data: entryQueryData } = useEntryByGiveawayIdQuery({
    variables: {
      giveawayId: giveaway?.id ?? "",
    },
    skip: !giveaway,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  })

  /**
   * Handlers
   */
  const handleGiveawayOpened = (
    updatedGiveaway: GiveawayFieldsFragment,
  ) => {
    apolloClient.cache.updateQuery<
      OpenGiveawaysQuery,
      OpenGiveawaysQueryVariables
    >(
      {
        query: OpenGiveawaysDocument,
        variables: {
          broadcastId: channel.broadcastId ?? "",
        },
      },
      (previousData) => {
        if (!previousData) return previousData

        return {
          giveaways_giveaways: {
            ...previousData.giveaways_giveaways,
            records: [
              updatedGiveaway,
              ...previousData.giveaways_giveaways.records.filter(
                (record) => record.id !== updatedGiveaway.id,
              ),
            ],
          },
        }
      },
    )

    addLocalNotification({
      text: "The host has started a giveaway",
      icon: "GiftSolid",
    })
  }

  const handleGiveawayClosed = (
    updatedGiveaway: GiveawayFieldsFragment,
  ) => {
    apolloClient.cache.updateQuery<
      OpenGiveawaysQuery,
      OpenGiveawaysQueryVariables
    >(
      {
        query: OpenGiveawaysDocument,
        variables: {
          broadcastId: channel.broadcastId ?? "",
        },
      },
      (previousData) => {
        if (!previousData) return previousData

        return {
          giveaways_giveaways: {
            ...previousData.giveaways_giveaways,
            records: previousData.giveaways_giveaways.records.filter(
              (record) => record.id !== updatedGiveaway.id,
            ),
          },
        }
      },
    )

    addLocalNotification({
      text: "The giveaway has ended",
      icon: "GiftSolid",
    })
  }

  const handleEntryDrawn = (drawnEntry: EntryFieldsFragment) => {
    const text = drawnEntry.isMe
      ? "You won the giveaway 🎉"
      : `${drawnEntry.firstName} won the giveaway 🎉`

    addLocalNotification({
      text,
      icon: "GiftSolid",
      highlight: true,
    })
  }

  const handleEntryCreated = (createdEntry: EntryFieldsFragment) => {
    apolloClient.writeQuery<
      EntryByGiveawayIdQuery,
      EntryByGiveawayIdQueryVariables
    >({
      query: EntryByGiveawayIdDocument,
      variables: {
        giveawayId: createdEntry.giveawayId,
      },
      data: { giveaways_entryByGiveawayId: createdEntry },
    })

    addLocalNotification({
      text: `You've entered the giveaway`,
      icon: "GiftSolid",
    })
  }

  /**
   * Subscriptions
   */
  useGiveawayEventSubscription({
    skip: !channel.broadcastId,
    variables: {
      broadcastId: channel.broadcastId,
      giveawayId: undefined,
    },
    onData: ({ data }) => {
      const event = data.data.giveaways_giveawayEvent

      if (
        event.type === Giveaways_GiveawayEventType.GiveawayStatusChanged
      ) {
        if (event.giveaway.status === Giveaways_GiveawayStatus.Open) {
          handleGiveawayOpened(event.giveaway)
        } else {
          handleGiveawayClosed(event.giveaway)
        }
      }
    },
  })

  useEntryEventSubscription({
    skip: !channel.broadcastId,
    variables: {
      broadcastId: channel.broadcastId ?? "",
      giveawayId: undefined,
    },
    onData: ({ data }) => {
      const event = data.data.giveaways_entryEvent

      if (event.type === Giveaways_EntryEventType.EntryDrawn) {
        handleEntryDrawn(event.entry)
      }
    },
  })

  /**
   * Mutations
   */
  const enterGiveawayMutation = useCreateEntryMutation({
    onCompleted: (data) => {
      handleEntryCreated(data.giveaways_createEntry)
    },
    optimisticResponse: (variables) => ({
      giveaways_createEntry: {
        // Generate a random ID until the mutation resolves.
        id: Math.random().toString().slice(2),
        drawnAt: null,
        firstName: variables.entry.firstName,
        isMe: true,
        giveawayId: variables.giveawayId,
        __typename: "Giveaways_Entry",
      },
    }),
  })

  return (
    <GiveawaysContext.Provider
      value={{
        giveaway,
        entry: entryQueryData?.giveaways_entryByGiveawayId ?? null,
        mutations: {
          enterGiveaway: enterGiveawayMutation,
        },
      }}
    >
      {children}
    </GiveawaysContext.Provider>
  )
}

/**
 * Hook to access giveaway state and operations.
 */
const useGiveawaysContext = () => {
  const context = useContext(GiveawaysContext)
  if (!context) {
    throw new Error(
      "No GiveawaysContext.Provider found when calling useGiveawaysContext.",
    )
  }
  return context
}

export { GiveawaysContext, GiveawaysProvider, useGiveawaysContext }
